/**
 * VehicleFinanceiroTab - Aba "Financeiro" do modal premium de veículo
 *
 * v2.0.0: refatorada para usar exatamente o mesmo layout/componentes da aba
 * Financeiro do modal de cliente (`CustomerFinancialTab`):
 *   - Sub-abas "Cobranças" e "Recorrência" com contadores
 *   - Tabelas unificadas (ChargesTableView / SubscriptionsTable)
 *   - Coluna "Proprietário" identificando o dono do documento na emissão
 * Mantém apenas os cards totalizadores (Em aberto / Pago) já existentes,
 * alimentados pela RPC `get_vehicle_financeiro`.
 *
 * @version 2.1.0
 * RELEASE: 2026-08-19 v2.1.0 - Repassa canEdit/canDelete para a sub-aba de Recorrência
 * RELEASE: 2026-08-19 v2.0.0 - Refatoração completa no padrão do modal de cliente
 *          (sub-abas + tabelas unificadas); remove bloco recolhível de histórico
 * RELEASE: 2026-08-18 v1.1.0 - Bloco recolhível de proprietários anteriores
 * RELEASE: 2026-08-18 v1.0.1 - Badges com rótulos reais do enum
 * RELEASE: 2026-08-18 v1.0.0 - Criação inicial (Financeiro x Veículos)
 */

import { useState } from 'react';
import { Wallet, Repeat } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useVehicleFinanceiro } from '@/hooks/useFinanceiroVehicles';
import { useFinanceiroCobrancasPaginated } from '@/hooks/useFinanceiroCobrancasPaginated';
import { useFinanceiroAssinaturasPaginated } from '@/hooks/useFinanceiroAssinaturasPaginated';
import { VehicleChargesTab } from '@/components/vehicles/modal/VehicleChargesTab';
import { VehicleSubscriptionsTab } from '@/components/vehicles/modal/VehicleSubscriptionsTab';

interface VehicleFinanceiroTabProps {
  vehicleId: string;
  /** Aba visível (evita fetch desnecessário) */
  isActive?: boolean;
  /** Permissão para editar cobrança */
  canEdit?: boolean;
  /** Permissão para excluir/cancelar cobrança */
  canDelete?: boolean;
}

type SubTab = 'charges' | 'subscriptions';

/** Formata valores monetários em BRL */
const formatBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

export function VehicleFinanceiroTab({
  vehicleId,
  isActive = true,
  canEdit = true,
  canDelete = true,
}: VehicleFinanceiroTabProps) {
  const [subTab, setSubTab] = useState<SubTab>('charges');

  // Totalizadores (Em aberto / Pago) — mantidos do layout anterior
  const { data } = useVehicleFinanceiro(vehicleId, isActive);

  // Contadores leves (pageSize 1, só para totalCount)
  const { totalCount: chargesCount } = useFinanceiroCobrancasPaginated(
    isActive ? { vehicle_id: vehicleId } : undefined,
    { page: 1, pageSize: 1 },
  );

  const { totalCount: subscriptionsCount } = useFinanceiroAssinaturasPaginated(
    isActive ? { vehicle_id: vehicleId } : undefined,
    { page: 1, pageSize: 1 },
  );

  return (
    <div className="flex flex-col h-full min-h-[520px]">
      {/* KPIs: total em aberto e total pago (mantidos da versão anterior) */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border bg-amber-500/5 p-3">
          <p className="text-[11px] text-muted-foreground">Em aberto</p>
          <p className="text-lg font-semibold">{formatBRL(data?.total_aberto ?? 0)}</p>
        </div>
        <div className="rounded-lg border bg-emerald-500/5 p-3">
          <p className="text-[11px] text-muted-foreground">Pago</p>
          <p className="text-lg font-semibold">{formatBRL(data?.total_pago ?? 0)}</p>
        </div>
      </div>

      {/* Sub-abas no mesmo padrão do modal de cliente */}
      <Tabs
        value={subTab}
        onValueChange={v => setSubTab(v as SubTab)}
        className="flex flex-col flex-1 min-h-0"
      >
        <TabsList className="mt-3 mb-2 grid w-fit grid-cols-2 h-9">
          <TabsTrigger value="charges" className="gap-2 text-xs">
            <Wallet className="h-3.5 w-3.5" />
            Cobranças
            <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-mono">
              {chargesCount}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="subscriptions" className="gap-2 text-xs">
            <Repeat className="h-3.5 w-3.5" />
            Recorrência
            <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-mono">
              {subscriptionsCount}
            </Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="charges" className="flex-1 overflow-hidden mt-0">
          <VehicleChargesTab
            vehicleId={vehicleId}
            isActive={isActive && subTab === 'charges'}
            canEdit={canEdit}
            canDelete={canDelete}
          />
        </TabsContent>

        <TabsContent value="subscriptions" className="flex-1 overflow-hidden mt-0">
          <VehicleSubscriptionsTab
            vehicleId={vehicleId}
            isActive={isActive && subTab === 'subscriptions'}
            canEdit={canEdit}
            canDelete={canDelete}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
